import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';

import { DocumentModel } from '@data-layer/models/documents';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { environment } from '@env/environment';
import { enumDocumentType } from '@data-layer/enums/documents';
import { ReplaySubject } from 'rxjs';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';

@Component({
  selector: 'document-upload-logo',
  templateUrl: './document-upload-logo.component.html',
  styleUrls: ['./document-upload-logo.component.scss'],
})
export class DocumentUploadLogoComponent implements OnDestroy {
  @Input() public uploadedDocument: DocumentModel;
  @Input() public id: string | number;
  @Input() public isLoading: boolean = false;
  @Output() public upload: EventEmitter<File> = new EventEmitter<File>();

  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);
  public errors = { maxFileSize: 'FORM_ERROR.MAX_FILE_SIZE', fileExtensionNotAllowed: 'FORM_ERROR.FILE_EXTENSION_NOT_ALLOWED' };
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;

  public isDragOver: boolean;
  public isUploadingDoc: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor() {}

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isUploadingDoc = true;
      this.uploadFile(files[0]);
    }
  }

  public get fileName(): string {
    return this.uploadedDocument && this.uploadedDocument.fileName ? this.uploadedDocument.fileName : null;
  }

  public onDragOver(event: DragEvent): void {
    if (!!this.fileName) {
      return;
    }
    this.isDragOver = true;
    event.preventDefault();
    event.stopPropagation();
  }

  public onDrop(event: DragEvent): void {
    if (!!this.fileName) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length) {
      this.isUploadingDoc = true;
      this.uploadFile(event.dataTransfer.files[0]);
    }
  }

  public onDragLeave(event: DragEvent): void {
    if (!!this.fileName) {
      return;
    }
    this.isDragOver = false;
    event.preventDefault();
    event.stopPropagation();
  }

  public clickInput(): void {
    this.clearFile();
  }

  private uploadFile(file: File): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      this.upload.emit(file);
    } else {
      this.isUploadingDoc = false;
    }
    this.inputElement.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }
}
